/**
 * A/B Test Cookie Utilities
 * Handles client and server-side cookie management for A/B test variants
 */

const COOKIE_NAME = "ab_test_variant";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days in seconds
const COOKIE_PATH = "/";

/**
 * Client-side: Get A/B test variant from cookie
 * @returns Variant ID if found, null otherwise
 */
export function getABTestVariant(): string | null {
	if (typeof document === "undefined") return null;

	const cookies = document.cookie.split(";");
	for (const cookie of cookies) {
		const [name, value] = cookie.trim().split("=");
		if (name === COOKIE_NAME && value) {
			return decodeURIComponent(value);
		}
	}
	return null;
}

/**
 * Client-side: Set A/B test variant in cookie
 * @param variant - Variant ID to store
 */
export function setABTestVariant(variant: string): void {
	if (typeof document === "undefined") return;

	const isProduction = process.env.NODE_ENV === "production";
	const expires = new Date();
	expires.setTime(expires.getTime() + COOKIE_MAX_AGE * 1000);

	const cookieValue = `${COOKIE_NAME}=${encodeURIComponent(variant)}; path=${COOKIE_PATH}; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${isProduction ? "; Secure" : ""}`;
	document.cookie = cookieValue;
}

/**
 * Client-side: Clear A/B test variant cookie
 */
export function clearABTestVariant(): void {
	if (typeof document === "undefined") return;

	document.cookie = `${COOKIE_NAME}=; path=${COOKIE_PATH}; max-age=0; SameSite=Lax`;
}

/**
 * Server-side: Get A/B test variant from cookies
 * Call this from server components or server actions
 * @returns Variant ID if found, null otherwise
 * 
 * Usage:
 * import { cookies } from "next/headers";
 * const cookieStore = await cookies();
 * const variant = cookieStore.get(COOKIE_NAME)?.value || null;
 */
export const AB_TEST_COOKIE_NAME = COOKIE_NAME;

